import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarDays, CheckCircle2, MapPin, Users } from "lucide-react";
import RegisterInterest from "@/components/RegisterInterest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSection() {
  const [infoDays, setInfoDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // SlotAvailability obj
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    topic: "",
    party_size: 1,
    additional_names: [], // length = party_size - 1
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const fetchDays = async () => {
    const { data } = await axios.get(`${API}/info-days`);
    return data;
  };

  const refreshAfterAction = async () => {
    try {
      const data = await fetchDays();
      setInfoDays(data);
      setSelectedDay((prev) => {
        const match = prev ? data.find((d) => d.id === prev.id) : null;
        return match || data[0] || null;
      });
    } catch (e) {
      /* swallow — booking has already succeeded */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchDays();
        if (cancelled) return;
        setInfoDays(data);
        if (data.length) setSelectedDay(data[0]);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load information days. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePartySize = (size) => {
    const n = Math.max(1, Math.min(20, Number(size) || 1));
    setForm((f) => ({
      ...f,
      party_size: n,
      additional_names: Array.from({ length: n - 1 }, (_, i) => f.additional_names[i] || ""),
    }));
  };

  const updateAdditionalName = (idx, value) => {
    setForm((f) => {
      const next = [...f.additional_names];
      next[idx] = value;
      return { ...f, additional_names: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Read directly from DOM — defensive against browser autofill not firing onChange
    const formEl = e.currentTarget;
    const domName = (formEl.querySelector('[data-testid="booking-full-name"]')?.value || form.full_name).trim();
    const domEmail = (formEl.querySelector('[data-testid="booking-email"]')?.value || form.email).trim();
    const domPhone = (formEl.querySelector('[data-testid="booking-phone"]')?.value || form.phone).trim();

    if (!selectedDay) {
      toast.error("Please choose a day.");
      return;
    }
    if (!selectedSlot) {
      toast.error("Please pick a time slot.");
      return;
    }
    if (domName.length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(domEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const namesForSubmit = [domName, ...form.additional_names.map((n) => n.trim())];
    if (namesForSubmit.length !== form.party_size || namesForSubmit.some((n) => n.length < 2)) {
      toast.error(`Please enter all ${form.party_size} attendee name(s).`);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        full_name: domName,
        email: domEmail,
        phone: domPhone || undefined,
        info_day_id: selectedDay.id,
        time_slot: selectedSlot.time,
        topic: form.topic.trim() || undefined,
        party_size: form.party_size,
        attendee_names: namesForSubmit,
      });
      setConfirmed(data);
      toast.success("Your slot is booked — we'll see you on the day.");
      setForm({ full_name: "", email: "", phone: "", topic: "", party_size: 1, additional_names: [] });
      setSelectedSlot(null);
      refreshAfterAction();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed.");
      refreshAfterAction(); // refresh capacities in case slot just filled up
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" className="bg-[#F2EFE8] border-y border-[#E0DCD3]" data-testid="booking-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-10">
          <div className="lg:col-span-5 reveal">
            <p className="overline mb-4">Information days</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight">
              Meet the team in person and ask anything you want.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 reveal">
            <p className="text-[15px] leading-relaxed text-[#2A2B2A]/85">
              We held two in‑person information days on <strong>24 &amp; 25 June 2026</strong>{" "}
              at the Loughrea Hotel &amp; Spa, and we’re now running a <strong>third day
              on Thursday 20 August 2026</strong> for anyone who wasn’t able to make the
              first two. Each 30‑minute slot is a private session with the project
              team — pick a free time slot and tell us who’s coming.
            </p>
          </div>
        </div>

        {/* Venue card — always visible */}
        {!loading && infoDays.length > 0 && (
          <div className="mb-10 bg-white border border-[#E0DCD3] rounded-sm p-6 lg:p-8 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="venue-card">
            <div className="md:col-span-7 flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#F2EFE8] border border-[#E0DCD3] shrink-0">
                <MapPin className="h-4 w-4 text-[#3A4B3A]" strokeWidth={1.8} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#CBA365]">Venue</p>
                <p className="font-serif-display text-xl tracking-tight mt-1">Loughrea Hotel &amp; Spa</p>
                <p className="text-[13.5px] text-[#63635E] mt-1">Athenry Rd, Loughrea, Co. Galway H62 Y189</p>
                <a
                  href="https://maps.google.com/?q=Loughrea+Hotel+%26+Spa,+Athenry+Rd,+Loughrea,+Co.+Galway+H62+Y189"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="venue-map-link"
                  className="inline-block mt-2 text-[12.5px] text-[#3A4B3A] link-underline"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>
            <div className="md:col-span-5 flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#F2EFE8] border border-[#E0DCD3] shrink-0">
                <Users className="h-4 w-4 text-[#3A4B3A]" strokeWidth={1.8} />
              </span>
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#CBA365]">Format</p>
                <p className="font-serif-display text-xl tracking-tight mt-1">30‑min private sessions</p>
                <p className="text-[13.5px] text-[#63635E] mt-1">
                  Each slot is a private session with the project team.
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white border border-[#E0DCD3] rounded-sm p-6 flex items-center gap-3 text-[#63635E]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : infoDays.length === 0 ? (
          <RegisterInterest />
        ) : confirmed ? (
          <div
            className="bg-white border border-[#E0DCD3] rounded-sm p-8 lg:p-12 flex items-start gap-5 reveal in-view"
            data-testid="booking-confirmation"
          >
            <CheckCircle2 className="h-7 w-7 text-[#3A4B3A] shrink-0 mt-1" />
            <div className="grow">
              <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#CBA365]">Booking confirmed</p>
              <h3 className="font-serif-display text-2xl tracking-tight mt-2">
                See you on {confirmed.info_day_label.split("—")[0].trim()}.
              </h3>
              <p className="mt-3 text-[14.5px] text-[#2A2B2A]/85">
                We’ve reserved <strong>{confirmed.time_slot}</strong> for{" "}
                <strong>{confirmed.party_size} {confirmed.party_size === 1 ? "person" : "people"}</strong> at the{" "}
                <strong>Loughrea Hotel &amp; Spa</strong>. A copy of the details has been sent
                to our Community Liaison team — they may be in touch ahead of the day. Your
                booking reference is{" "}
                <span className="font-mono text-[13px] text-[#4C4538]">{confirmed.id.slice(0, 8)}</span>.
              </p>

              {confirmed.attendee_names?.length > 0 && (
                <div className="mt-5">
                  <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#4C4538]">Attendees</p>
                  <ul className="mt-2 text-[14px] text-[#2A2B2A] list-disc list-inside space-y-1">
                    {confirmed.attendee_names.map((n, i) => (
                      <li key={i} data-testid={`confirmed-attendee-${i}`}>{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => setConfirmed(null)}
                data-testid="booking-make-another"
                className="mt-6 inline-flex items-center h-10 px-4 rounded-sm border border-[#4C4538] text-[#4C4538] text-sm font-medium hover:bg-[#4C4538] hover:text-white transition-colors"
              >
                Book another slot
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* DAY + SLOT PICKER */}
            <div className="lg:col-span-5">
              <p className="overline mb-3">1 · Choose a day</p>
              <div className="grid gap-3" data-testid="info-day-list">
                {infoDays.map((d) => {
                  const active = selectedDay?.id === d.id;
                  const slotsLeft = d.slots.filter((s) => s.available).length;
                  return (
                    <button
                      key={d.id}
                      onClick={() => {
                        setSelectedDay(d);
                        setSelectedSlot(null);
                      }}
                      data-testid={`info-day-${d.id}`}
                      className={`text-left rounded-sm p-5 border transition-colors ${
                        active
                          ? "bg-[#3A4B3A] text-white border-[#3A4B3A]"
                          : "bg-white border-[#E0DCD3] hover:border-[#8C9B86]"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarDays className={`h-4 w-4 ${active ? "text-white" : "text-[#3A4B3A]"}`} />
                        <span className={`text-[11px] tracking-[0.18em] uppercase font-semibold ${active ? "text-white/80" : "text-[#CBA365]"}`}>
                          {d.time_window}
                        </span>
                      </div>
                      <div className="font-serif-display text-lg tracking-tight">{d.date_label}</div>
                      <div className={`text-[13px] mt-1 ${active ? "text-white/80" : "text-[#63635E]"}`}>
                        {d.venue} · {slotsLeft} of {d.slots.length} slot{d.slots.length === 1 ? "" : "s"} available
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedDay && (
                <>
                  <p className="overline mt-8 mb-3">2 · Pick a 30‑min slot</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="slot-list">
                    {selectedDay.slots.map((s) => {
                      const active = selectedSlot?.time === s.time;
                      const taken = !s.available;
                      return (
                        <button
                          key={s.time}
                          onClick={() => !taken && setSelectedSlot(s)}
                          disabled={taken}
                          data-testid={`slot-${s.time.replace(/\s/g, "")}`}
                          className={`text-left px-3 py-2.5 rounded-sm border transition-colors ${
                            taken
                              ? "bg-[#F2EFE8] border-[#E0DCD3] text-[#63635E] cursor-not-allowed opacity-60"
                              : active
                              ? "bg-[#4C4538] text-white border-[#4C4538]"
                              : "bg-white border-[#E0DCD3] hover:border-[#8C9B86]"
                          }`}
                        >
                          <div className="text-[12.5px] font-medium">{s.time}</div>
                          <div className={`text-[10.5px] mt-0.5 ${
                            active ? "text-white/80" : taken ? "text-[#A89A82]" : "text-[#63635E]"
                          }`}>
                            {taken ? "Booked" : "Available"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* FORM */}
            <form
              onSubmit={onSubmit}
              className="lg:col-span-7 bg-white border border-[#E0DCD3] rounded-sm p-7 lg:p-10"
              data-testid="booking-form"
            >
              <p className="overline mb-5">3 · Your details</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name *">
                  <input
                    required
                    minLength={2}
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="booking-full-name"
                    className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="booking-email"
                    className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="booking-phone"
                    className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                  />
                </Field>
                <Field label="Total in your group (including yourself)">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={form.party_size}
                    onChange={(e) => updatePartySize(e.target.value)}
                    data-testid="booking-party-size"
                    className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                  />
                </Field>
              </div>

              {form.party_size > 1 && (
                <div className="mt-6" data-testid="additional-names-block">
                  <p className="text-[12px] font-medium text-[#4C4538] mb-2.5">
                    Names of the {form.party_size - 1} other {form.party_size - 1 === 1 ? "person" : "people"} joining you *
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {form.additional_names.map((nm, idx) => (
                      <input
                        key={idx}
                        required
                        minLength={2}
                        type="text"
                        value={nm}
                        onChange={(e) => updateAdditionalName(idx, e.target.value)}
                        placeholder={`Person ${idx + 2}`}
                        data-testid={`additional-name-${idx}`}
                        className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5">
                <Field label="Topic or question (optional)">
                  <textarea
                    rows={4}
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    placeholder="e.g. screening near my property, traffic during construction, hedgerow planting…"
                    data-testid="booking-topic"
                    className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30 resize-none"
                  />
                </Field>
              </div>

              {selectedSlot && !selectedSlot.available && (
                <p className="mt-4 text-[13px] text-[#A0341E]" data-testid="capacity-warning">
                  This slot was just booked by someone else. Please pick another slot.
                </p>
              )}

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-[13px] text-[#63635E]" data-testid="booking-summary">
                  {selectedDay && selectedSlot ? (
                    <>
                      Selected: <strong className="text-[#2A2B2A]">{selectedDay.date_label}</strong>,{" "}
                      <strong className="text-[#2A2B2A]">{selectedSlot.time}</strong>
                    </>
                  ) : (
                    <>Pick a day and a time slot to enable booking.</>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-sm bg-[#3A4B3A] text-white text-sm font-medium hover:bg-[#2A3B2A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Booking…" : "Confirm my slot"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-[#4C4538] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
